// @utils/usePermission.js
import { useMemo, useContext } from "react";
import { PermissionContext } from "@layout/AppLayout"; // ✅ Import your context

/** 🔹 Recursive search for a permission by menuLink or menuID */
function findPermissionRecursively(obj, keyOrId) {
  for (const [key, value] of Object.entries(obj)) {
    if (!value || typeof value !== "object") continue;

    const { menuLink, menuID, subMenu } = value;

    // ✅ Direct match by key, menuLink, or menuID
    if (
      key === keyOrId ||
      String(menuLink).toLowerCase() === String(keyOrId).toLowerCase() ||
      String(menuID) === String(keyOrId)
    ) {
      return value;
    }

    // ✅ Search in subMenu recursively (fix)
    if (Array.isArray(subMenu) && subMenu.length > 0) {
      const found = findPermissionRecursively(
        Object.fromEntries(subMenu.map((m) => [m.menuLink, m])),
        keyOrId
      );
      if (found) return found;
    }
  }
  return null;
}


/** 🔹 If direct permission not found, find parent module that contains it */
function findParentPermission(obj, childKey) {
  for (const [, value] of Object.entries(obj)) {
    if (!value || typeof value !== "object") continue;
    const { subMenu } = value;

    if (Array.isArray(subMenu)) {
      const child = subMenu.find(
        (m) =>
          String(m.menuLink).toLowerCase() === String(childKey).toLowerCase() ||
          String(m.menuID) === String(childKey)
      );
      if (child) return value; // parent found
    }
  }
  return null;
}

/** 🔹 Default fallback when no permission found */
function defaultPermission() {
  return {
    module_access: "no",
    view: "no",
    add: "no",
    edit: "no",
    delete: "no",
  };
}

/** ✅ Hook — get permission from cached context */
export function usePermission(keyOrId) {
  /////const cachedPermissions = useContext(PermissionContext);
  const { permissions: cachedPermissions } = useContext(PermissionContext);
  console.log("permissions",keyOrId);
  console.log(cachedPermissions);
  console.log(window.__permissionsCache);
  
  return useMemo(() => {
    if (!cachedPermissions || !keyOrId) return defaultPermission();

    let perm = findPermissionRecursively(cachedPermissions, keyOrId);

    // ✅ Fallback to parent (for subpages like /whatsapp/integration)
    if (!perm) perm = findParentPermission(cachedPermissions, keyOrId);

    return perm || defaultPermission();
  }, [keyOrId, cachedPermissions]);
}

/** ✅ Utility function (useful outside React) */
export function canUser(keyOrId, action = "view", permissionsObj = null) {
  const all = permissionsObj || window.__permissionsCache || {};
  if (!all) return false;

  let perm = findPermissionRecursively(all, keyOrId);
  if (!perm) perm = findParentPermission(all, keyOrId);
  if (!perm) return false;

  return (
    perm.module_access === "yes" &&
    (perm[action] === "yes" || action === "module_access")
  );
}

/** Extracts first segment from route path (useful in routes) */
export function extractModuleKeyFromPath(path) {
  if (!path) return "";
  const segments = path.split("/").filter(Boolean);
  return segments[0] || "";
}

export function getAllPermissions() {
  try {
    const stored = localStorage.getItem("userPermission");
    return stored ? JSON.parse(stored) : {};
  } catch (err) {
    console.error("Error parsing userPermission from localStorage:", err);
    return {};
  }
}